<script setup lang="ts">
/**
 * 🔴🔴 全局错误边界。
 *
 * <h2>它捕获什么</h2>
 * <ul>
 *   <li>子组件树中抛出的**未处理异常**（渲染期 / 生命周期 / 事件处理器）；
 *   <li><code>setup()</code> 中的同步异常与 <code>async setup()</code> 的 rejected Promise。</li>
 * </ul>
 *
 * <h2>它捕获不到什么</h2>
 * <ul>
 *   <li>事件回调里 <code>setTimeout</code> / <code>requestAnimationFrame</code> 中的异常 ——
 *       它们在 Vue 的调度循环之外，需要业务自己 <code>try/catch</code>；</li>
 *   <li><code>watchEffect</code> 的 async 部分抛出的异常 —— Vue 只捕获 sync 部分。</li>
 * </ul>
 * ⇒ 这两类在 <code>ApiErrorAlert.vue</code> 里用 <code>message.error()</code> 兜底。
 *
 * <h2>交互</h2>
 * 捕获后：
 * <ol>
 *   <li>把异常写到 console（开发期排查）；</li>
 *   <li>渲染一个**降级 UI**：一行错误摘要 + 「刷新页面」按钮 ——
 *       不展示堆栈给用户看（堆栈只在 console 里）。</li>
 * </ol>
 * 🔴 不做「自动重试」—— 渲染期异常多半是状态损坏，重试只会再炸一次。
 */
import { onErrorCaptured, ref } from 'vue'

const hasError = ref(false)
const errorMessage = ref('')

onErrorCaptured((err: unknown) => {
  hasError.value = true
  errorMessage.value = err instanceof Error ? err.message : String(err)
  // 🔴 写到 console —— 生产里用户不会看，开发期能立刻定位
  console.error('[ErrorBoundary] 捕获到未处理异常：', err)
  // 返回 false 阻止异常继续向上传播（到 app.config.errorHandler）
  return false
})

function reload() {
  // 最简单的恢复方式：刷新页面。Vue 3 的 ErrorBoundary 无法「重置组件树」
  // （不像 React 的 getDerivedStateFromError），所以刷新是唯一可靠的恢复手段。
  window.location.reload()
}
</script>

<template>
  <template v-if="hasError">
    <div class="error-boundary">
      <div class="error-card">
        <div class="error-icon">⚠️</div>
        <h2>页面出了点问题</h2>
        <p class="error-message">{{ errorMessage }}</p>
        <p class="error-hint">已将错误记录到控制台。刷新页面通常可以恢复。</p>
        <button class="reload-btn" @click="reload">刷新页面</button>
      </div>
    </div>
  </template>
  <slot v-else />
</template>

<style scoped>
.error-boundary {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 24px;
}

.error-card {
  max-width: 420px;
  text-align: center;
  padding: 32px 24px;
  border-radius: 12px;
  background: var(--card-bg, #fff);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-card h2 {
  margin: 0 0 12px;
  font-size: 20px;
}

.error-message {
  color: #d03050;
  font-family: monospace;
  font-size: 13px;
  word-break: break-all;
  margin: 0 0 8px;
}

.error-hint {
  color: var(--text-color-3, #999);
  font-size: 14px;
  margin: 0 0 20px;
}

.reload-btn {
  padding: 8px 24px;
  border: none;
  border-radius: 6px;
  background: #2080f0;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}

.reload-btn:hover {
  background: #4098fc;
}
</style>
